import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { switchMap, take, tap, map } from 'rxjs/operators';
import { JobService } from '../job.service';
import { LoginService } from '@intranet/modules/login/login.service';
import { UserService } from '@intranet/modules/users/user.service';
import { User, UserOrGroup, UserGroup } from '@intranet/modules/users/user.types';
import { JobAction } from '@intranet/modules/actions/action.types';
import { Job, JobStatus, JobType, StatusDetail } from '../job.types';
import { combineLatest, Observable, of } from 'rxjs';
import { Client, ClientsService } from '@intranet/modules/clients';
import { orderClientsByGroupName } from '@intranet/modules/clients/client.helpers';
import { HotkeyService } from 'src/app/core/services/hotkey.service';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';

@Component({
    selector: 'jobs-manage',
    templateUrl: './jobs-manage.component.html',
    styleUrls: ['./jobs-manage.component.scss'],
})
export class JobsManageComponent implements OnInit, AfterViewInit {
    @Input() clients: Client[] = [];
    @Input() jobId: number | null;
    @Input() showDialog: boolean;
    @Output() changed = new EventEmitter();
    @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('jobTitleField') jobTitleField: ElementRef;
    @ViewChild('jobForm') jobForm: NgForm;

    submitting = false;
    job: Job;
    jobTypes: JobType[];
    users: User[];
    filterClients: [Client[], Client[]] = [[], []];
    allClients: Client[] = [];
    isGroup: boolean = false;
    defaultApproval: JobAction | undefined;
    defaultInvoice: JobAction | undefined;
    disableClientSelection = false;
    _jobOwner: UserOrGroup;
    allowCompleted: boolean = true;
    error: string;
    title: string;
    groups: UserGroup[] = [];
    jobStatuses: StatusDetail[] = [
        new StatusDetail(JobStatus.Live, 'Live'),
        new StatusDetail(JobStatus.Archived, 'Archived'),
    ];
    saveIntent: 'close' | 'open' | null = null;
    private originalClientId: number | null = null;


    get jobOwner(): UserOrGroup {
        return this._jobOwner;
    }
    set jobOwner(uog: UserOrGroup) {
        this._jobOwner = uog;
        if (uog.isUser) {
            this.job.jobOwner = uog.user;
        }
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected dialog: MatDialog,
        private jobService: JobService,
        private loginService: LoginService,
        private userService: UserService,
        private clientService: ClientsService,
        private hotkeyService: HotkeyService
    ) {}

    ngOnInit(): void {
        // Register hotkey for "Cancel" action (Ctrl + Q)
        this.hotkeyService.registerHotkey('ctrl+q', 'Cancel action', () => this.onCancel());

        // Register hotkey for "Save" action (Ctrl + Alt + S)
        this.hotkeyService.registerHotkey('ctrl+alt+s', 'Save action', () => {
            if (
                this.showDialog &&
                !this.submitting &&
                this.saveIntent === null &&
                this.jobForm?.valid &&
                !this.jobForm?.pristine
              ) {
                if (!this.job?.jobId) {
                    // Add job modal
                    this.onSaveIntent('close');
                    this.onSubmit();
                } else {
                    // Edit job modal
                    this.onSubmit();
                }
            }
        });

        // Register hotkey for "Save and Open" action (Ctrl + O)
        this.hotkeyService.registerHotkey('ctrl+o', 'Save and Open action', () => {
            if (
                this.showDialog &&
                !this.submitting &&
                this.saveIntent === null &&
                this.jobForm?.valid &&
                !this.jobForm?.pristine &&
                !this.job?.jobId // Only for "Add job" modal
            ) {
                this.onSaveIntent('open');
                this.onSubmit();
            }
        });

        let observables$: Observable<any>[] = [];
        observables$.push(this.route.paramMap);
        observables$.push(this.jobService.jobTypes$);
        observables$.push(this.userService.users$);

        // If we are not explicitly passed in clients, we need to populate
        if (!this.clients.length) {
            observables$.push(this.clientService.clients$);
            observables$.push(this.clientService.myClients$);
        } else {
            observables$.push(of(this.clients));
        }

        combineLatest(observables$)
            .pipe(
                take(1),
                tap(([paramMap, jobTypes, users, allClients, myClients]) => {
                    if (allClients) {
                        this.allClients = allClients;

                        if (allClients.length > 1) {
                            const [pinnedClients, restClients] =
                                orderClientsByGroupName(
                                    allClients,
                                    myClients?.map((c) => c.clientId) ?? [],
                                    true
                                );

                            this.filterClients = [
                                pinnedClients as Client[],
                                restClients as Client[],
                            ];
                        } else {
                            this.filterClients = [[], allClients];
                        }
                    }

                    this.jobTypes = jobTypes;
                    this.users = users.filter((user) => user.active);
                }),
                map(([paramMap]) => paramMap as ParamMap),
                switchMap((p) => {
                    let id = p.get('id');
                    if (id) {
                        return this.jobService.getJob(+id);
                    } else {
                        // Modal alternative
                        if (this.jobId) {
                            return this.jobService.getJob(this.jobId);
                        }
                    }
                    return of(<Job>{
                        jobStatus: JobStatus.Live,
                        jobType: {},
                        active: true,
                        jobOwnerId: this.loginService.userId,
                    });
                })
            )
            .subscribe({
                next: (job) => {
                    if (!job.archive && !job.canArchive) {
                        this.allowCompleted = false;
                    }

                    if (job.archive) {
                        job.jobStatus = JobStatus.Archived;
                    } else {
                        job.jobStatus = JobStatus.Live;
                    }

                    if (!job.jobStatus) {
                        job.jobStatus = JobStatus.Live;
                    }

                    this.job = job;
                    this.job.jobTypeId = this.job.jobType?.jobTypeId;
                    this.originalClientId = job.clientId ?? null;

                    // By default billable on creation
                    if (!this.job.jobId) {
                        this.job.isBillable = true;
                    }

                    if (!this.job?.jobId) {
                        if (this.clients?.length === 1) {
                            // Client page: If only one client, preselect and disable selection
                            const only = this.clients[0];
                            this.job.clientId = only.clientId;
                            this.disableClientSelection = true;
                        } else if (this.jobService.filterQuery?.clientId) {
                            // If a specific clientId was explicitly filtered, respect it
                            const preselected = this.allClients.find(
                              c => c.clientId === this.jobService.filterQuery.clientId
                            );
                            if (preselected) {
                              this.job.clientId = preselected.clientId;
                              this.disableClientSelection = true;
                            }
                        }
                        // keep confirmation logic sane for Add flow
                        this.originalClientId = this.job.clientId ?? this.originalClientId;
                    }

                    if (this.job && this.job.jobId) {
                        this.title = 'Edit job';
                    }
                    if (!this.job.jobId) {
                        this.title = 'Add job';
                        this.job.jobOwner = this.userService.users.find(
                            (u) => u.id === this.loginService.userId
                        );
                    }
                },
                error: (error) => {
                    this.error = error;
                },
            });

        this.userService.groups$
            .pipe(take(1))
            .subscribe(g => this.groups = g);
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.jobTitleField?.nativeElement?.focus(), 0);
    }

    onSaveIntent(saveIntent: 'close' | 'open') {
        this.saveIntent = saveIntent;
    }

    onOwnerSelected(selection: User | string | null) {
        if (selection && typeof selection !== 'string') {
          // regular user
          this.job.jobOwnerId = selection.id;
          this.job.jobOwner = selection;
        } else if (typeof selection === 'string' && selection.startsWith('group-')) {
          // group – handle as you need, e.g. store the string
          this.job.jobOwnerId = null;
          // maybe save group selection somewhere else?
        } else {
          // cleared
          this.job.jobOwnerId = null;
          this.job.jobOwner = null;
        }
    }

    onSubmit() {
        // Return if already submitting
        if (this.submitting) return;

        this.submitting = true;

        if (!this.job.jobId) {
            this.job.jobActions = [];
            if (this.defaultApproval) {
                this.job.jobActions.push(this.defaultApproval);
            }
            if (this.defaultInvoice) {
                this.job.jobActions.push(this.defaultInvoice);
            }
        }

        this.job.archive = this.job.jobStatus === JobStatus.Archived;

        this.jobService.addOrUpdateJob(this.job).subscribe({
            next: (result) => {
                if (!result.success) {
                    this.error = result.error;
                } else {
                    if (!this.jobId && this.saveIntent === 'open') {
                        this.router.navigateByUrl(
                            `/jobs/detail/${result.job.jobId}?triggerAddAction=true`
                        );
                    } else if (this.showDialog) {
                        this.onChanged();
                    } else {
                        this.router.navigate([
                            `/clients/dashboard/${result.job.clientId}`,
                        ]);
                    }
                }
            },
            error: (error) => {
                this.error = error;
            },
            complete: () => {
                this.submitting = false;
            },
        });
    }

    onChanged() {
        // Retrieves showDialog input, turns to false and returns back to parent in order to close modal
        this.showDialog = false;
        this.changed.emit(this.showDialog);
    }

    onCancel() {
        // Retrieves showDialog input, turns to false and returns back to parent in order to close modal
        this.showDialog = false;
        this.close.emit(this.showDialog);
    }

    onDeleteJob() {
        const dialogRef = this.dialog.open(AlertDialog, {
            width: '320px',
            data: {
                title: 'Are you sure?',
                message: `You are about to delete this job. This operation cannot be undone. Are you sure?`,
                confirm: true,
                confirmTitle: 'Yes',
            },
        });

        dialogRef
            .afterClosed()
            .pipe(
                switchMap((result) => {
                    if (!result) {
                        return of(null);
                    }
                    return this.jobService.deleteJob(this.jobId);
                }),
                tap(response => {
                    if (response && response.error) {
                        this.dialog.open(AlertDialog, {
                            width: '320px',
                            data: {
                                title: 'Cannot delete job',
                                message: response.error,
                                confirm: true,
                                confirmTitle: 'OK',
                            },
                        });
                    } else if (response) {
                        this.router.navigate(['/jobs/listing']);
                    }
                })
            )
            .subscribe();
    }

    onClientChange(newClientId: number | string) {
        if (!this.job) return;
        if (newClientId == null || newClientId === '') return;
        
        const id = typeof newClientId === 'string' ? parseInt(newClientId, 10) : newClientId;

        if (this.job?.jobId && this.originalClientId !== id) {
            const dialogRef = this.dialog.open(AlertDialog, {
                width: '320px',
                data: {
                    title: 'Are you sure?',
                    message: 'You are about to change the client. Are you sure you want to proceed?',
                    confirm: true,
                    confirmTitle: 'Yes',
                },
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.job.clientId = id;
                    this.originalClientId = id;
                } else {
                    this.job.clientId = this.originalClientId!;
                }
            });
        } else {
            this.job.clientId = id;
            this.originalClientId = id;
        }
    }

    sortJobTypes(prop: string) {
        const sorted = this.jobTypes.sort((a, b) =>
            a[prop] > b[prop] ? 1 : a[prop] === b[prop] ? 0 : -1
        );
        // asc/desc
        if (prop.charAt(0) === '-') {
            sorted.reverse();
        }
        return sorted;
    }
}
